import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { Col } from 'react-bootstrap';
import {
  EXPENSE_BONUS_BOOKS,
  EXPENSE_BONUS_SMART_WORKING,
  EXPENSE_BOOKS_BUDGET,
  PERMISSIONS,
} from '../../constants';
import { TrainingDaysDataGrid } from './TrainingDaysDataGrid';
import { UserContext } from '../../context/UserContext';
import { Unauthorized } from '../../components/Unauthorized';
import { useContext, useEffect, useMemo, useState } from 'react';
import { TrainingExpensesDataGrid } from './TrainingExpensesDataGrid';
import { TabWithChart } from './TabWithChart';
import { getExpensesByTypeAndYear } from '../../utils/API/expenses';
import _ from 'lodash';
import { getTrainingDays, getTrainingExpenses } from '../../utils/API/budgets';
import { MonoSelectUser } from '../../components/Form/MonoSelectUser';
import { EmployeeListContext } from '../../context/EmployeeListContext';
import { BudgetContext } from '../../context/BudgetContext';
import { renderSpinner } from '../../utils/Utility';
import { useTranslation } from 'react-i18next';
import { BonusExpensesDataGrid } from './BonusExpensesDataGrid';

const getExpenses = async (type, year, username) => {
  const expensesData = await getExpensesByTypeAndYear(type, year, username);
  return _.orderBy(expensesData, ['date'], ['asc']);
};

const getClass = (available, max) =>
  `percent-${Math.max(0, Math.floor((100 * available) / max))}`;

export const BudgetPage = () => {
  const { t } = useTranslation();
  const { user, hasClaim } = useContext(UserContext);
  const canSeeOtherUsers = hasClaim(PERMISSIONS.BUDGET_USER_SELECTION);
  const isForbidden = !hasClaim(PERMISSIONS.BUDGET_VIEW) && !canSeeOtherUsers;
  const { employeesQueryA: users, employeesByIdQueryA: usersById } =
    useContext(EmployeeListContext);
  const { id: loggedUserId } = user;
  const [impersonatedUserId, setImpersonatedUserId] = useState(loggedUserId);
  const selectedUserData = useMemo(
    () => usersById[impersonatedUserId] || user,
    [usersById, impersonatedUserId, user]
  );
  const username = selectedUserData?.username;
  const defaultYear = new Date().getFullYear();
  const { year, setYear, configuration, availableYears } =
    useContext(BudgetContext);

  useEffect(() => {
    const savedYear = localStorage.getItem('budget_year');
    if ((!year || year === 0) && availableYears && availableYears.length > 0) {
      if (savedYear && availableYears.includes(Number(savedYear))) {
        setYear(Number(savedYear));
      } else if (availableYears.includes(defaultYear)) {
        setYear(defaultYear);
      } else {
        setYear(availableYears[0]);
      }
    }
  }, [year, setYear, availableYears, defaultYear]);

  useEffect(() => {
    if (year) {
      localStorage.setItem('budget_year', year);
    }
  }, [year]);

  const [booksBudget, setBooksBudget] = useState(0);
  const [smartWorkingBudget, setSmartWorkingBudget] = useState(0);

  const [bookExpenses, setBookExpenses] = useState([]);
  const [smartExpenses, setSmartExpenses] = useState([]);
  const [bookExpensesNew, setBookExpensesNew] = useState([]);
  const [smartExpensesNew] = useState([]);
  const [trainingDays, setTrainingDays] = useState({
    limit: 0,
    balance: 0,
    training_day: [],
  });
  const [trainingExpenses, setTrainingExpenses] = useState({
    limit: 0,
    balance: 0,
    training_budget: [],
  });

  const [loadingCount, setLoadingCount] = useState(0);
  const decreaseLoadingCount = () => setLoadingCount((n) => n - 1);
  useEffect(() => {
    if (!username) {
      return;
    }

    setLoadingCount(4);

    const companyId = selectedUserData?.company ?? user.company;
    setBooksBudget(
      configuration.booksBudget.find((i) => i.company === companyId)?.limit ?? 0
    );
    setSmartWorkingBudget(
      configuration.smartWorkingBudget.find((i) => i.company === companyId)
        ?.limit ?? 0
    );

    getTrainingDays(year, username)
      .then((data) =>
        setTrainingDays(data || { limit: 0, balance: 0, training_day: [] })
      )
      .catch(() => setTrainingDays({ limit: 0, balance: 0, training_day: [] }))
      .finally(decreaseLoadingCount);
    getTrainingExpenses(year, username)
      .then((data) =>
        setTrainingExpenses(
          data || { limit: 0, balance: 0, training_budget: [] }
        )
      )
      .catch(() =>
        setTrainingExpenses({ limit: 0, balance: 0, training_budget: [] })
      )
      .finally(decreaseLoadingCount);
    getExpenses(EXPENSE_BONUS_BOOKS, year, username)
      .then(setBookExpenses)
      .catch(() => setBookExpenses([]))
      .finally(decreaseLoadingCount);
    getExpenses(EXPENSE_BONUS_SMART_WORKING, year, username)
      .then(setSmartExpenses)
      .catch(() => setSmartExpenses([]))
      .finally(decreaseLoadingCount);
    getExpenses(EXPENSE_BOOKS_BUDGET, year, username)
      .then(setBookExpensesNew)
      .catch(() => setBookExpensesNew([]))
      .finally(decreaseLoadingCount);
  }, [impersonatedUserId, username, usersById, year]);

  const allBookExpenses = [...bookExpenses, ...bookExpensesNew];
  const availableBooksBudget = booksBudget - _.sumBy(allBookExpenses, 'cost');
  const allSmartExpenses = [...smartExpenses, ...smartExpensesNew];
  const availableSmartWorkingBudget =
    smartWorkingBudget - _.sumBy(allSmartExpenses, 'cost');

  return (
    <div className="content__wrapper">
      {renderSpinner(loadingCount > 0)}
      <div className="headerPage content">
        <div className="title">Budget formazione e altri bonus</div>
        <div className="spacer" />
        <div className="d-flex gap-3 form-group">
          {canSeeOtherUsers && (
            <MonoSelectUser select={setImpersonatedUserId} users={users} />
          )}
          <select
            className="yearContainer"
            value={year}
            onChange={({ target: { value } }) => {
              setYear(+value);
              localStorage.setItem('budget_year', value);
            }}
          >
            {availableYears.map((n) => (
              <option value={+n} key={+n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
      {isForbidden ? (
        <Unauthorized />
      ) : (
        <div className="content__overflow">
          <div className="tabsContainer">
            <Col md="12">
              <Tabs className="mb-3 tabs-chart" justify mountOnEnter>
                <Tab
                  eventKey="training_budget"
                  tabClassName={getClass(
                    trainingExpenses.balance,
                    trainingExpenses.limit
                  )}
                  title={
                    <TabWithChart
                      title="Budget formazione"
                      maxAvailable={trainingExpenses.limit}
                      available={trainingExpenses.balance}
                    />
                  }
                >
                  <TrainingExpensesDataGrid
                    expenses={trainingExpenses.training_budget}
                    available={trainingExpenses.balance}
                    budget={trainingExpenses.limit}
                  />
                </Tab>
                <Tab
                  eventKey="training_days"
                  tabClassName={getClass(
                    trainingDays.balance,
                    trainingDays.limit
                  )}
                  title={
                    <TabWithChart
                      title="Giorni di formazione"
                      unit="giorni"
                      maxAvailable={trainingDays.limit}
                      available={trainingDays.balance}
                    />
                  }
                >
                  <TrainingDaysDataGrid
                    days={trainingDays.training_day}
                    available={trainingDays.balance}
                    budget={trainingDays.limit}
                  />
                </Tab>
                <Tab
                  eventKey="book"
                  tabClassName={getClass(availableBooksBudget, booksBudget)}
                  title={
                    <TabWithChart
                      title={t('bonus_books')}
                      maxAvailable={booksBudget}
                      available={availableBooksBudget}
                    />
                  }
                >
                  <BonusExpensesDataGrid
                    expenses={allBookExpenses}
                    available={availableBooksBudget}
                    budget={booksBudget}
                  />
                </Tab>
                <Tab
                  eventKey="smart_working"
                  tabClassName={getClass(
                    availableSmartWorkingBudget,
                    smartWorkingBudget
                  )}
                  title={
                    <TabWithChart
                      title={t('bonus_smart_working')}
                      maxAvailable={smartWorkingBudget}
                      available={availableSmartWorkingBudget}
                    />
                  }
                >
                  <BonusExpensesDataGrid
                    expenses={allSmartExpenses}
                    available={availableSmartWorkingBudget}
                    budget={smartWorkingBudget}
                  />
                </Tab>
              </Tabs>
            </Col>
          </div>
        </div>
      )}
    </div>
  );
};
